import { NavigateFunction, useNavigate } from "react-router-dom";

import { iconTypeHS, projectTypeHS } from "sdecontent";
import scraperApi from "api";
import Toaster from "components/Toaster";
import ActionsMenu, { ActionMenuItem } from "components/ActionsMenu";
import { ProjectConfigWithStats } from "providers/HostedScrapingProvider/types";
import { useHostedScrapingProjects } from "providers/HostedScrapingProvider";
import { ProjectStatus } from "./ProjectStatus";
import { formatDate } from "./ProjectFormatDate";
import { projectIcon } from "components/hosted-scraping/ProjectIcon";

const lineClassName = 'font-normal text-sm text-gray dark:text-neutral-600';
const headerClasses = 'font-droid text-slate-400 text-xs font-medium';

type IProjectNameAndTypeProps = {
  project: ProjectConfigWithStats,
}

const ProjectNameAndType = ({project}: IProjectNameAndTypeProps) => {
  const Icon = projectIcon(iconTypeHS(project.config.type));
  return (
  <div className="flex flex-row flex-start h-16 pt-3 pb-3 pl-2.5">
    <div className="w-10 h-10 p-1 border border-slate-200 flex items-center justify-center">
      <Icon/>
    </div>
    <div className="flex flex-col pl-2">
      <div className="font-medium text-base text-gray-700 dark:text-neutral-700 overflow-hidden whitespace-nowrap">{project.name}</div>
      <div className="font-normal text-xs text-slate-400 overflow-hidden whitespace-nowrap">{projectTypeHS(project.config.type)}</div>
    </div>
  </div>
)};

const navigateToProject = (navigate: NavigateFunction, projectId: number) => () => {
  navigate(`/projects/${encodeURIComponent(projectId)}`);
}

const projectLine = (
  navigate: NavigateFunction,
  project: ProjectConfigWithStats,
  onCancelScheduling: (project: ProjectConfigWithStats) => void,
) => {
  const actions: ActionMenuItem[] = [
    { label: "Edit", onClick: () => navigate(`/projects/${project.id}/edit`) },
    {
      label: "Cancel Scheduling",
      onClick: () => onCancelScheduling(project),
      disabled: !project.enabled,
      destructive: true,
    },
  ];

  return (
    <tr onClick={navigateToProject(navigate, project.id)} className="hover:bg-slate-50 cursor-pointer" key={project.id}>
      <td className={lineClassName}><ProjectNameAndType project={project}/></td>
      <td className={lineClassName}><ProjectStatus project={project}/></td>
      <td className={lineClassName}>{formatDate(project.lastRunAt)}</td>
      <td className={lineClassName}>{project.credits}</td>
      <td className={lineClassName} onClick={(e) => e.stopPropagation()}>
        <ActionsMenu items={actions} ariaLabel="Project actions" />
      </td>
    </tr>
  );
};

const projectListHeader = () => (
  <tr className="text-left">
    <th className={headerClasses}>PROJECT</th>
    <th className={headerClasses}>STATUS</th>
    <th className={headerClasses}>LAST RUN</th>
    <th className={headerClasses}>CREDITS SPENT</th>
    <th className={headerClasses}>ACTIONS</th>
  </tr>
)

type IProjectListProps = {
  projects: ProjectConfigWithStats[],
}


export default function ProjectListView({projects}: IProjectListProps) {
  const navigate = useNavigate();
  const { refreshInBackground } = useHostedScrapingProjects();

  // Disabling scheduling is a project update with scheduling switched off — same
  // shape the edit page uses (enabled: false, no next run). Stats are read-only
  // derived fields, so strip them from the payload.
  const handleCancelScheduling = async (project: ProjectConfigWithStats) => {
    const {
      lastRunAt, credits, creditsInBillingPeriod, allJobsCount, doneJobsCount,
      errorJobsCount, cancelledJobsCount, successfulTasks, failedTasks,
      ...projectConfig
    } = project;

    try {
      await scraperApi.hostedScraping.updateProject({
        ...projectConfig,
        enabled: false,
        supposedToRunAt: undefined,
      });
      Toaster.success("Scheduling disabled successfully");
      refreshInBackground(undefined);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to disable scheduling";
      Toaster.error(errorMessage);
    }
  };

  return (
    <div className="w-full bg-white border border-solid border-slate-200">
      <div className="p-6 w-full overflow-auto">
          <table className="w-full divide-y divide-slate-200">
            <thead>
              {projectListHeader()}
            </thead>
            <tbody className="w-full divide-y divide-slate-200">
              {projects.map((project) => projectLine(navigate, project, handleCancelScheduling))}
            </tbody>
          </table>
      </div>
    </div>
  );
}
